import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { Plus, Search, Eye, Edit2, Trash2, X, Bell } from 'lucide-react';
import toast from 'react-hot-toast';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

const MODES    = ['Letter','Email','WhatsApp','Phone Call','In Person','Meeting','Other'];
const INIT_STATUS = ['Submitted','Under Review','Approved','Rejected','Need Clarification','In Progress','Pending'];
const FINAL_STATUS = ['Open','Closed'];

const empty = {
  submitted_date: new Date().toISOString().split('T')[0],
  submitted_by: '', request_mode: 'Letter',
  issue_details: '', action_taken_by: '',
  initial_status: 'Submitted', final_status: 'Open',
  closed_date: '', remarks: '',
};

export default function Concerns() {
  const { isEditor, canEdit } = useAuth();
  const [records,    setRecords]    = useState([]);
  const [staffs,     setStaffs]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [fStatus,    setFStatus]    = useState('');
  const [fFinal,     setFFinal]     = useState('Open');
  const [fSubmitted, setFSubmitted] = useState('');
  const [fAction,    setFAction]    = useState('');
  const [fMode,      setFMode]      = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [showView,   setShowView]   = useState(null);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(empty);
  const [saving,     setSaving]     = useState(false);

  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { load(); }, []);

  // Real-time updates (debounced)
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { try { load(); } catch(e){} }, 800); };
    const ch = supabase.channel('concerns-realtime')
      .on('postgres_changes', {event:'*', schema:'public', table:'concerns_requests'}, () => _reload())
      .on('postgres_changes', {event:'*', schema:'public', table:'notifications'}, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);


  async function load() {
    try {
      const [{ data: r, error: re }, { data: s }] = await Promise.all([
        supabase.from('concerns_requests').select('*,submitted_staff:submitted_by(staff_name),action_staff:action_taken_by(staff_name)').order('submitted_date', { ascending: false }),
        supabase.from('hr_staff').select('id,staff_name').eq('is_active', true).order('staff_name'),
      ]);
      if (re) { console.error('concerns_requests:', re.message); }
      setRecords(r || []);
      setStaffs(s || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  async function save() {
    if (!form.submitted_by || !form.issue_details.trim()) {
      toast.error('Submitted By and Issue Details are required'); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      closed_date: form.final_status === 'Closed' ? (form.closed_date || new Date().toISOString().split('T')[0]) : null,
    };
    const { error } = editing
      ? await supabase.from('concerns_requests').update(payload).eq('id', editing)
      : await supabase.from('concerns_requests').insert(payload);

    if (error) { toast.error(error.message); setSaving(false); return; }

    // Notify the action_taken_by staff if assigned
    if (form.action_taken_by && form.action_taken_by !== form.submitted_by) {
      await supabase.from('notifications').insert({
        staff_id: form.action_taken_by,
        title: '📋 Concern/Request Assigned to You',
        message: `A concern/request has been assigned to you for action: "${form.issue_details.slice(0, 80)}${form.issue_details.length > 80 ? '...' : ''}"`,
        type: 'concern',
        reference_id: null,
      });
    }

    toast.success(editing ? 'Updated ✅' : 'Concern submitted ✅');
    setShowModal(false); setEditing(null); setForm(empty); load();
    setSaving(false);
  }

  async function del(id) {
    if (!window.confirm('Delete this record?')) return;
    await supabase.from('concerns_requests').delete().eq('id', id);
    toast.success('Deleted'); load();
  }

  function openNew()  { setEditing(null); setForm(empty); setShowModal(true); }
  function openEdit(r){ setEditing(r.id); setForm({ ...empty, ...r, submitted_by: r.submitted_by||'', action_taken_by: r.action_taken_by||'' }); setShowModal(true); }

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    return (!q         || r.issue_details?.toLowerCase().includes(q) || r.submitted_staff?.staff_name?.toLowerCase().includes(q) || r.action_staff?.staff_name?.toLowerCase().includes(q))
      && (!fStatus     || r.initial_status  === fStatus)
      && (!fFinal      || r.final_status    === fFinal)
      && (!fSubmitted  || r.submitted_by    === fSubmitted)
      && (!fAction     || r.action_taken_by === fAction)
      && (!fMode       || r.request_mode    === fMode);
  });

  const openCount   = records.filter(r => r.final_status === 'Open').length;
  const closedCount = records.filter(r => r.final_status === 'Closed').length;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title">📋 Concerns & Requests</h2>
          <p className="page-subtitle">Track internal issues, concerns and requests</p>
        </div>
        {canEdit('concerns') && <button className="btn btn-primary" onClick={openNew}><Plus size={14}/> New Concern / Request</button>}
      </div>

      {/* Summary */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {[
          { label: 'Total',  value: records.length,  color: 'var(--accent)' },
          { label: 'Open',   value: openCount,        color: 'var(--red)' },
          { label: 'Closed', value: closedCount,      color: 'var(--green)' },
          { label: 'Pending Action', value: records.filter(r=>r.final_status==='Open'&&!r.action_taken_by).length, color: 'var(--amber)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters — compact card */}
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,padding:'10px 12px',marginBottom:14}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr auto auto auto',gap:6,alignItems:'center',marginBottom:6}}>
          <div className="search-bar" style={{margin:0}}>
            <Search size={13}/>
            <input placeholder="Search issue, staff..." value={search} onChange={e=>setSearch(e.target.value)} style={{fontSize:12}}/>
          </div>
          <select style={{fontSize:11,padding:'3px 6px',height:28}} value={fFinal} onChange={e=>setFFinal(e.target.value)}>
            <option value="">All</option>
            <option value="Open">🔴 Open</option>
            <option value="Closed">✅ Closed</option>
          </select>
          {(search||fStatus||fFinal||fSubmitted||fAction||fMode)
            ? <button className="btn btn-ghost btn-sm" style={{fontSize:11,padding:'2px 8px',height:28}} onClick={()=>{setSearch('');setFStatus('');setFFinal('');setFSubmitted('');setFAction('');setFMode('');}}>✕ Clear</button>
            : <div/>}
          <span style={{fontSize:11,color:'var(--text3)',whiteSpace:'nowrap'}}>{filtered.length} records</span>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6}}>
          <select style={{fontSize:11,padding:'3px 6px',height:28,width:'100%'}} value={fSubmitted} onChange={e=>setFSubmitted(e.target.value)}>
            <option value="">👤 Submitted By</option>
            {staffs.map(s=><option key={s.id} value={s.id}>{s.staff_name}</option>)}
          </select>
          <select style={{fontSize:11,padding:'3px 6px',height:28,width:'100%'}} value={fAction} onChange={e=>setFAction(e.target.value)}>
            <option value="">⚡ Action Taken By</option>
            {staffs.map(s=><option key={s.id} value={s.id}>{s.staff_name}</option>)}
          </select>
          <select style={{fontSize:11,padding:'3px 6px',height:28,width:'100%'}} value={fMode} onChange={e=>setFMode(e.target.value)}>
            <option value="">📨 Request Mode</option>
            {MODES.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
          <select style={{fontSize:11,padding:'3px 6px',height:28,width:'100%'}} value={fStatus} onChange={e=>setFStatus(e.target.value)}>
            <option value="">📌 Initial Status</option>
            {INIT_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Submitted By</th>
              <th>Mode</th>
              <th>Issue / Request</th>
              <th>Action By</th>
              <th>Status</th>
              <th>Final</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9}><div className="loading"><div className="spinner"/></div></td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={9}><div className="empty-state"><h3>No records found</h3></div></td></tr>}
            {filtered.map((r, i) => (
              <tr key={r.id}>
                <td style={{ fontSize: 12, color: 'var(--text3)' }}>{i + 1}</td>
                <td style={{ fontSize: 12 }}>{fmtDMY(r.submitted_date)}</td>
                <td style={{ fontWeight: 500, fontSize: 13 }}>{r.submitted_staff?.staff_name || '—'}</td>
                <td><span className="badge badge-blue" style={{ fontSize: 10 }}>{r.request_mode}</span></td>
                <td style={{ maxWidth: 280, fontSize: 13 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }} title={r.issue_details}>
                    {r.issue_details}
                  </div>
                </td>
                <td style={{ fontSize: 12 }}>{r.action_staff?.staff_name || <span style={{ color: 'var(--text3)' }}>Unassigned</span>}</td>
                <td>
                  <span className={`badge ${r.initial_status==='Approved'?'badge-green':r.initial_status==='Rejected'?'badge-red':r.initial_status==='Submitted'?'badge-blue':'badge-amber'}`} style={{ fontSize: 10 }}>
                    {r.initial_status}
                  </span>
                </td>
                <td>
                  <span className={`badge ${r.final_status==='Closed'?'badge-green':'badge-red'}`} style={{ fontSize: 10 }}>
                    {r.final_status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-ghost btn-sm" title="View" onClick={() => setShowView(r)}><Eye size={13}/></button>
                    {canEdit('concerns') && <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => openEdit(r)}><Edit2 size={13}/></button>}
                    {canEdit('concerns') && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} title="Delete" onClick={() => del(r.id)}><Trash2 size={13}/></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      {showView && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowView(null)}>
          <div className="modal" style={{ maxWidth: 580 }}>
            <div className="modal-header">
              <span className="modal-title">📋 Concern / Request Details</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowView(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              {[
                ['Submitted Date',   showView.submitted_date],
                ['Submitted By',     showView.submitted_staff?.staff_name],
                ['Request Mode',     showView.request_mode],
                ['Issue / Details',  showView.issue_details],
                ['Action Taken By',  showView.action_staff?.staff_name || 'Unassigned'],
                ['Initial Status',   showView.initial_status],
                ['Final Status',     showView.final_status],
                ['Closed Date',      showView.closed_date || '—'],
                ['Remarks',          showView.remarks || '—'],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ width: 140, fontWeight: 600, fontSize: 12, color: 'var(--text2)', flexShrink: 0 }}>{label}</div>
                  <div style={{ fontSize: 13, color: 'var(--text)', flex: 1, whiteSpace: 'pre-wrap' }}>{val}</div>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              {canEdit('concerns') && <button className="btn btn-secondary" onClick={() => { setShowView(null); openEdit(showView); }}>Edit</button>}
              <button className="btn btn-primary" onClick={() => setShowView(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal modal-lg" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit' : 'New'} Concern / Request</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Submitted Date *</label>
                  <DateInput value={form.submitted_date} onChange={e => sf('submitted_date', e.target.value)}/>
                </div>
                <div className="form-group"><label>Submitted By *</label>
                  <select value={form.submitted_by} onChange={e => sf('submitted_by', e.target.value)}>
                    <option value="">— Select Staff —</option>
                    {staffs.map(s => <option key={s.id} value={s.id}>{s.staff_name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Request Mode</label>
                  <select value={form.request_mode} onChange={e => sf('request_mode', e.target.value)}>
                    {MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Action Taken By</label>
                  <select value={form.action_taken_by} onChange={e => sf('action_taken_by', e.target.value)}>
                    <option value="">— Select Staff —</option>
                    {staffs.map(s => <option key={s.id} value={s.id}>{s.staff_name}</option>)}
                  </select>
                  {form.action_taken_by && form.action_taken_by !== form.submitted_by && (
                    <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}>
                      <Bell size={10}/> A notification will be sent to this staff when saved
                    </div>
                  )}
                </div>
                <div className="form-group full"><label>Issue / Concern / Request Details *</label>
                  <textarea value={form.issue_details} onChange={e => sf('issue_details', e.target.value)}
                    rows={4} placeholder="Describe the issue, concern or request in detail..."/>
                </div>
                <div className="form-group"><label>Initial Status</label>
                  <select value={form.initial_status} onChange={e => sf('initial_status', e.target.value)}>
                    {INIT_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Final Status</label>
                  <select value={form.final_status} onChange={e => sf('final_status', e.target.value)}
                    style={{ borderColor: form.final_status === 'Closed' ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                    {FINAL_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {form.final_status === 'Closed' && (
                  <div className="form-group"><label>Closed Date</label>
                    <DateInput value={form.closed_date} onChange={e => sf('closed_date', e.target.value)}/>
                  </div>
                )}
                <div className="form-group full"><label>Remarks</label>
                  <textarea value={form.remarks} onChange={e => sf('remarks', e.target.value)} rows={3} placeholder="Any additional remarks or notes..."/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Update' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
